import {
  BalancesApiGiftCard,
  BalancesApiGiftCardTransaction,
  GiftCard,
  GiftCardTransaction,
} from '@vend/types'
import { add } from 'vend-number'
import { OmniGiftCardSettings } from './gift-cards-types'
import { ApiGiftCardSettings, GraphQLGiftCard } from './gift-cards'
import { HydratedGiftCardTransaction } from './modal/ViewDetails'

/**
 * Takes a BalancesApiGiftCard and returns a standard GiftCard.
 */
export const giftCardFromBalancesApi = (gc: BalancesApiGiftCard): GiftCard => {
  return {
    id: gc.id,
    code: gc.number,
    createdAt: gc.created_at,
    expiresAt: gc.expires_at,
    status: gc.status,
    balance: gc.balance,
    initialAmount: gc.total_sold,
    transactions: transactionsFromBalancesApiTransactions(
      gc.gift_card_transactions
    ),
  }
}

export const giftCardSettingsFromBalancesApi = (
  apiGiftCardSettings: ApiGiftCardSettings
): OmniGiftCardSettings => {
  return {
    expiry_period: apiGiftCardSettings.expiry_period,
    expiry_period_type: apiGiftCardSettings.expiry_period_type,
    minValue: apiGiftCardSettings.min_value || 0.01,
    maxValue: apiGiftCardSettings.max_value || undefined,
    sell_enabled: apiGiftCardSettings.sell_enabled,
    redeem_enabled: apiGiftCardSettings.redeem_enabled,
    predefined_amounts: apiGiftCardSettings.predefined_amounts || [],
    channel: apiGiftCardSettings.channel,
  }
}

const transactionsFromBalancesApiTransactions = (
  transactions: BalancesApiGiftCardTransaction[]
): GiftCardTransaction[] => {
  return transactions.map(t => ({
    id: t.id,
    amount: t.amount,
    type: t.type,
    userId: t.user_id,
    clientId: t.client_id,
    createdAt: t.created_at,
  }))
}

/**
 * Convert an GraphQLGiftCard to a standard GiftCard
 * @param apiGiftCard
 */
export const giftCardFromGraphQLGiftCard = (
  apiGiftCard: GraphQLGiftCard | null
): GiftCard | null => {
  if (!apiGiftCard) {
    return null
  }
  const { id, code, createdAt, expiresAt, status } = apiGiftCard
  return {
    id,
    code,
    createdAt,
    expiresAt,
    status,
    initialAmount: Number(apiGiftCard.initialAmount.amountRounded),
    balance: Number(apiGiftCard.balance.amountRounded),
    // GraphQL currently does not return transactions, as the only
    // implementation at this time is BigCommerce, which has no
    // transactions. If we merge Balances into GraphQL then this
    // will need to be added.
    transactions: [],
  }
}

/**
 * Orders transactions and calculates balances at the time of the transaction.
 * This is copied from abstract-gift-cards.ts
 */
export const calculateAndSortTransactionsBalance = (
  transactions: HydratedGiftCardTransaction[]
) => {
  const giftCardTransactions = [...transactions].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  giftCardTransactions.forEach((transaction: any, index: number) => {
    if (index === 0) {
      // First transaction is the gift card's activation
      transaction.balance = transaction.amount
    } else {
      transaction.balance = add(
        giftCardTransactions[index - 1].balance,
        transaction.amount
      )
    }
  })

  return giftCardTransactions.reverse()
}
